import type { QueryClient } from '@tanstack/react-query';
import type { CallOutcome, CallType } from '@/api/types';
import {
  handleAccepted,
  handleAnsweredElsewhere,
  handleCallTaken,
  handleDeclined,
  handleEnded,
  handleEndingSoon,
  handleExtended,
  handleIncoming,
  handleProviderSession,
} from '@/state/callStore';
import { registerBinding } from './bindings';
import { tabs } from './tabChannel';

/** Call signaling from `/ws/calls` (contracts/realtime-events.md) into the call store. */
const isString = (value: unknown): value is string => typeof value === 'string';
const record = (value: unknown) =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : null;

registerBinding('calls', 'call:incoming', (payload) => {
  const p = record(payload);
  if (!p || !isString(p.callId) || !isString(p.callerId)) return;
  handleIncoming({
    callId: p.callId,
    callerId: p.callerId,
    callType: p.callType === 'video' ? 'video' : ('audio' as CallType),
    callerName: isString(p.callerName) ? p.callerName : undefined,
    callerPhotoUrl: isString(p.callerPhotoUrl) ? p.callerPhotoUrl : undefined,
  });
});

registerBinding('calls', 'call:accepted', (payload) => {
  const p = record(payload);
  if (p && isString(p.callId)) handleAccepted({ callId: p.callId });
});

registerBinding('calls', 'call:declined', (payload) => {
  const p = record(payload);
  if (p && isString(p.callId)) handleDeclined({ callId: p.callId });
});

registerBinding('calls', 'call:provider-session', (payload) => {
  const p = record(payload);
  if (p && isString(p.callId) && isString(p.providerSessionId)) {
    handleProviderSession({
      callId: p.callId,
      providerSessionId: p.providerSessionId,
      endsAt: isString(p.endsAt) ? p.endsAt : undefined,
    });
  }
});

// A call lasts 30 minutes: a warning shortly before the end, and either person can extend it.
registerBinding('calls', 'call:ending-soon', (payload) => {
  const p = record(payload);
  if (p && isString(p.callId) && isString(p.endsAt)) {
    handleEndingSoon({ callId: p.callId, endsAt: p.endsAt, canExtend: p.canExtend === true });
  }
});

registerBinding('calls', 'call:extended', (payload) => {
  const p = record(payload);
  if (p && isString(p.callId) && isString(p.endsAt)) {
    handleExtended({ callId: p.callId, endsAt: p.endsAt, canExtend: p.canExtend === true });
  }
});

registerBinding('calls', 'call:answered-elsewhere', (payload) => {
  const p = record(payload);
  if (p && isString(p.callId)) handleAnsweredElsewhere({ callId: p.callId });
});

registerBinding('calls', 'call:ended', (payload, { queryClient }) => {
  const p = record(payload);
  if (!p || !isString(p.callId)) return;
  handleEnded({
    callId: p.callId,
    outcome: (isString(p.outcome) ? p.outcome : 'completed') as CallOutcome,
    durationSeconds: typeof p.durationSeconds === 'number' ? p.durationSeconds : 0,
    reason: p.reason === 'time-limit' ? 'time-limit' : undefined,
  });
  // The call log and the missed-call rows in chats.
  void queryClient.invalidateQueries({ queryKey: ['callLog'] });
});

/** Another tab of this browser answered or declined: stop ringing here (data-model.md B3). */
export function startCallTabSync(_queryClient?: QueryClient): () => void {
  return tabs.subscribe((message) => {
    if (message.type === 'call:taken') handleCallTaken(message.callId);
  });
}
